import { Sheet } from "react-modal-sheet";

import { useTranslation } from "react-i18next";
import IconClose from "@/assets/icons/close.svg?react";
import SwitchHorizontal from "@/assets/icons/switch-horizontal.svg?react";
import DonLinear from "@/assets/icons/don-linear.svg?react";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { formatNumber } from "@/shared/utils";
import { useGetProfile } from "@/pages/profile/api";
import { useBuyChancesMutation } from "./buy-chances.api.ts";
import { Loader2 } from "lucide-react";
import ErrorMessage from "@/components/common/ErrorMessage.tsx";
import { useQueryClient } from "@tanstack/react-query";
import { ChallengeDetailKeys } from "../api/useChallengeDetail.ts";
import { ChallengeMemberKeys } from "../api/challenge-members.api.ts";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import { cn } from "@/lib/utils.ts";

const chanceSuggestions = [5, 10, 50, 100];

interface Props {
  chanceFee: string | number;
  challengeId: string;
  isPremium: boolean;
}

const BuyMoreChances = (props: Props) => {
  const { chanceFee, challengeId, isPremium } = props;
  const { data: profile, refetch } = useGetProfile();
  const [openDrawer, setOpenDrawer] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // useEffect(() => {
  //   if (openDrawer) {
  //     setTimeout(() => {
  //       inputRef.current!.focus();
  //     }, 400);
  //   }
  // }, [openDrawer]);

  const closeDrawer = () => {
    setOpenDrawer(false);
  };
  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const { t } = useTranslation();
  const [chanceValue, setChanceValue] = useState<number | string>("");

  const changeChanceValue = (num: number | string) => {
    setChanceValue(num);
  };
  const {
    mutateAsync: buyChances,
    isPending,
    isError,
  } = useBuyChancesMutation(Number(chanceValue));

  const isValidChanceValue = useMemo(() => {
    return (
      profile &&
      parseFloat(chanceFee.toString()) * Number(chanceValue) <=
        Number(profile.wallet_amount)
    );
  }, [chanceFee, chanceValue, profile]);

  const isValid = () => {
    if (!chanceValue) return false;
    if (Number(chanceValue) <= 0) return false;
    if (!profile) return false;
    if (!isValidChanceValue) return false;

    return true;
  };
  const queryClient = useQueryClient();

  const handleBuyChances = async () => {
    if (!isValid()) return;
    await buyChances({ challengeId, opportunities: Number(chanceValue) });
    queryClient.invalidateQueries({
      queryKey: [ChallengeDetailKeys.ChallengeDetail, challengeId],
    });
    queryClient.invalidateQueries({
      queryKey: [ChallengeMemberKeys.ChallengeMembers, challengeId],
    });
    setChanceValue("");
    closeDrawer();
    refetch();
  };

  const chanceValidation = useCallback(
    (value: number) => {
      if (!profile?.wallet_amount) return false;

      if (
        value * parseFloat(chanceFee.toString()) >
        parseFloat(profile.wallet_amount)
      ) {
        return false;
      }

      return true;
    },
    [profile, chanceFee]
  );

  const filteredChanceSuggestions = useMemo(() => {
    return chanceSuggestions.filter((number) => chanceValidation(number));
  }, [chanceValidation]);

  return (
    <Fragment>
      <BaseButton
        onClick={handleOpenDrawer}
        variant={isPremium ? "premium" : "primary"}
        text={t("challenge.buy_more_chances")}
        className={cn(
          "w-full h-11",
          isPremium && "shadow-[0_4.442px_22.208px_0_rgba(251,199,108,0.47)]"
        )}
        vibrate={true}
        vibrationType="soft"
      />
      <Sheet
        rootId="root"
        detent="content-height"
        isOpen={openDrawer}
        onClose={closeDrawer}
        onMouseDown={(e) => e.stopPropagation()}
        tweenConfig={{ ease: [0.16, 1, 0.3, 1], duration: 0.55 }}
        onOpenEnd={() => {
          setTimeout(() => {
            inputRef.current?.focus();
          }, 400);
        }}
      >
        <Sheet.Container className="!bg-gray-4 rounded-t-2xl">
          <Sheet.Header className="[&_.react-modal-sheet-header]:!py-2 [&_.react-modal-sheet-header]:!h-5 !bg-gray-4 rounded-t-2xl" />
          {/* resizes-content (index.html) shrinks the layout viewport on
              keyboard open, so this bottom sheet already sits above the
              keypad — no JS keyboardHeight padding (double-counted on Pro
              Max). pb-safe-bottom below covers the home indicator. */}
          <Sheet.Content className="bg-gray-4 px-4">
            <div className="pb-safe-bottom">
              <div className="flex items-center justify-between gap-3 pb-3">
                <h2 className="text-lg font-bold text-white">
                  {t("challenge.buy_more_chances")}
                </h2>
                <VibratingButton onClick={closeDrawer}>
                  <IconClose className="text-gray-1 size-6 cursor-pointer" />
                </VibratingButton>
              </div>
              <div className="flex flex-col items-center justify-center gap-3 mb-8 pt-4">
                {/* Action stuff */}
                <div className="flex justify-center flex-col items-center gap-1">
                  <h2 className="text-sm leading-130 text-gray-1">
                    {t("challenge.add_chances")}
                  </h2>
                  <div className="h-[62px] max-w-[90%] justify-center flex items-center">
                    {/* this input */}
                    <input
                      ref={inputRef}
                      className="text-3xl w-full h-[30px] caret-[#2684FC] border-none bg-transparent text-white flex justify-center text-center outline-none placeholder:text-white/08"
                      placeholder="0"
                      type="tel"
                      min={0}
                      value={chanceValue}
                      autoFocus
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/\D/g, ""); // remove all non-digits
                        changeChanceValue(numericValue);
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-1 max-w-sm overflow-hidden">
                    <SwitchHorizontal className="text-[#ACB7C1] size-4" />
                    <div className="flex items-center gap-0.5">
                      <DonLinear className="text-3" />
                      <p className="line-clamp-2 break-all">
                        {chanceValue
                          ? formatNumber(
                              parseFloat(chanceFee.toString()) *
                                Number(chanceValue)
                            )
                          : 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Chance suggestion */}
                {filteredChanceSuggestions.length > 0 && (
                  <div className="flex gap-2">
                    {filteredChanceSuggestions.map((number) => {
                      return (
                        <ChanceSuggestionCard
                          key={number}
                          number={number}
                          onClick={(num) => {
                            setChanceValue(num);
                            inputRef.current?.focus();
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
              {/* My balance */}
              {profile && <MyBalance balance={profile.wallet_amount} />}
              {/* Buy Chance */}
              <BaseButton
                onClick={handleBuyChances}
                prefix={isPending ? <Loader2 className="animate-spin" /> : null}
                text={
                  isValidChanceValue
                    ? t("challenge.buy_more_chances")
                    : t("challenge.lack_of_don")
                }
                vibrate={true}
                vibrationType="soft"
                className="w-full mt-3"
                disabled={!isValidChanceValue || isPending}
              />
              {isError && (
                <ErrorMessage>{t("challenge.buy_chances_error")}</ErrorMessage>
              )}
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop className="!bg-[#0E0E14]/80" onTap={closeDrawer} />
      </Sheet>
    </Fragment>
  );
  // return (
  //   <Drawer open={openDrawer} onOpenChange={(e) => setOpenDrawer(e)}>
  //     <DrawerTrigger asChild>
  //       <BaseButton
  //         onClick={handleOpenDrawer}
  //         variant="premium"
  //         text={t("challenge.buy_more_chances")}
  //         className="w-full h-11 shadow-[0_4.442px_22.208px_0_rgba(251,199,108,0.47)]"
  //       />
  //     </DrawerTrigger>
  //     <DrawerContent className="bg-gray-4" showTopThumb>
  //       <DrawerHeader className="flex justify-between items-center">
  //         <DrawerTitle className="text-lg" data-testid="title">
  //           {t("challenge.buy_more_chances")}
  //         </DrawerTitle>
  //         <DrawerClose>
  //           <IconClose className="size-6 text-gray-1 cursor-pointer" />
  //         </DrawerClose>
  //       </DrawerHeader>
  //       <DrawerBody>
  //         <div className="flex flex-col items-center justify-center gap-3 mb-8">
  //           {/* Action stuff */}
  //           <div className="flex justify-center flex-col items-center gap-1">
  //             <h2 className="text-sm leading-130 text-gray-1">
  //               {t("challenge.add_chances")}
  //             </h2>
  //             <div className="h-[62px] max-w-[90%] justify-center flex items-center">
  //               <input
  //                 className="text-5xl w-full caret-[#2684FC] border-none bg-transparent text-white flex justify-center text-center outline-none placeholder:text-white/08"
  //                 placeholder="0"
  //                 type="tel"
  //                 min={0}
  //                 value={chanceValue}
  //                 autoFocus
  //                 onChange={(e) => {
  //                   const numericValue = e.target.value.replace(/\D/g, ""); // remove all non-digits
  //                   changeChanceValue(numericValue);
  //                 }}
  //               />
  //             </div>
  //             <div className="flex items-center gap-1 max-w-sm overflow-hidden">
  //               <SwitchHorizontal className="text-[#ACB7C1] size-4" />
  //               <div className="flex items-center gap-0.5">
  //                 <DonLinear className="text-3" />
  //                 <p className="line-clamp-2 break-all">
  //                   {chanceValue
  //                     ? formatNumber(
  //                         parseFloat(chanceFee.toString()) * Number(chanceValue)
  //                       )
  //                     : 0}
  //                 </p>
  //               </div>
  //             </div>
  //           </div>

  //           {/* Chance suggestion */}
  //           {filteredChanceSuggestions.length > 0 && (
  //             <div className="flex gap-2">
  //               {filteredChanceSuggestions.map((number) => {
  //                 return (
  //                   <ChanceSuggestionCard
  //                     key={number}
  //                     number={number}
  //                     onClick={(num) => setChanceValue(num)}
  //                   />
  //                 );
  //               })}
  //             </div>
  //           )}
  //         </div>
  //         {/* My balance */}
  //         {profile && <MyBalance balance={profile.wallet_amount} />}
  //         {/* Buy Chance */}
  //         <BaseButton
  //           onClick={handleBuyChances}
  //           prefix={isPending ? <Loader2 className="animate-spin" /> : null}
  //           text={
  //             isValidChanceValue
  //               ? t("challenge.buy_more_chances")
  //               : t("challenge.lack_of_don")
  //           }
  //           className="w-full mt-3"
  //           disabled={!isValidChanceValue || isPending}
  //         />
  //         {isError && (
  //           <ErrorMessage>{t("challenge.buy_chances_error")}</ErrorMessage>
  //         )}
  //       </DrawerBody>
  //     </DrawerContent>
  //   </Drawer>
  // );
};

export default BuyMoreChances;

interface ChanceSuggestionCardProps {
  number: number;
  onClick?: (number: number) => void;
}

interface MyBalanceProps {
  balance: string | number;
}

const MyBalance = (props: MyBalanceProps) => {
  const { balance } = props;
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border-b border-white/08 bg-white/08 backdrop-blur-[10px] p-3 py-2.5 relative overflow-hidden h-[70px]">
      <p className="text-xs font-normal leading-130 text-gray-1 mb-2">
        {t("challenge.your_balance_is")}
      </p>
      <div className="flex items-center gap-2">
        <DonLinear className="size-[22px]" />
        <p className="font-bold text-2xl text-white leading-[124%]">
          {formatNumber(balance)}
        </p>
      </div>
      <img
        className="size-[217px] absolute -right-[1px] -top-[30px] z-0"
        src="images/patterns/challenge-info.webp"
        alt="Don pattern"
      />
    </div>
  );
};

const ChanceSuggestionCard = (props: ChanceSuggestionCardProps) => {
  const { number, onClick } = props;

  return (
    <VibratingButton
      onClick={() => onClick?.(number)}
      className="w-10 h-[30px] rounded-[40px] bg-white/08 flex items-center justify-center text-sm leading-130"
    >
      {number}
    </VibratingButton>
  );
};
